import { Users } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { Me } from '@/lib/api'

const SEEN = 'gatheredfrom:everyone-opened'

function seen(userId: string) {
  try {
    return localStorage.getItem(SEEN) === userId
  } catch {
    return false
  }
}

// Said once, when your first connection (someone you're not in a circle with) opens Everyone: what Everyone is now,
// that your own space stays yours, and that a circle is there for a closer group. Remembered on this device.
export function EveryoneOpens({ me }: { me: Me }) {
  const navigate = useNavigate()
  const [done, setDone] = useState(() => seen(me.id))
  if (!me.everyoneByConnection || done) return null
  function close(to?: string) {
    try {
      localStorage.setItem(SEEN, me.id)
    } catch {
      // Not remembered; it shows again next time.
    }
    setDone(true)
    if (to) navigate(to)
  }
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-md">
        <DialogHeader className="items-center text-center sm:text-center">
          <span aria-hidden className="grid size-14 place-items-center rounded-full bg-brand text-brand-foreground">
            <Users className="size-7" />
          </span>
          <DialogTitle className="text-title">You’re connected</DialogTitle>
          <DialogDescription>Everyone is open to you now.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-body">
          <p>
            When you share a photo, moment or recipe, you can choose <strong>Everyone</strong>, and everyone you’re
            connected to will see it. What they share with Everyone shows on your Everyone page.
          </p>
          <p>Your own space is still just yours. Nothing there is shared unless you choose to share it.</p>
          <p className="text-muted-foreground">
            Want a closer group, like your family or a club? Make a circle and invite them in. You can do it any time.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={() => close('/everyone')}>See Everyone</Button>
          <Button variant="outline" onClick={() => close('/circle/new')}>
            Create a circle
          </Button>
          <Button variant="ghost" onClick={() => close()}>
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
